<template>
  <div class="area-cadastro p-4">
    <h5 class="bold mb-4">
      <i class="fas fa-user-edit"></i>
      Meus Dados Cadastrais
    </h5>

    <!-- Loading -->
    <div v-if="isLoading" class="text-center py-5">
      <img src="@/assets/images/loading.gif" class="is-loading" />
      <p class="text-muted mt-3">Carregando dados...</p>
    </div>

    <div v-else>
      <form @submit.prevent="alterarDados">
        <!-- Dados Pessoais -->
        <div class="section-container mb-4">
          <div class="section-header">
            <i class="fas fa-user"></i>
            <span>Dados Pessoais</span>
          </div>

          <div class="row">
            <div class="col-md-8 mb-3">
              <FieldInput 
                label="Nome Completo" 
                type="text" 
                :required="true" 
                v-model="dados.nome"
                placeholder="Informe seu nome completo"
              />
            </div>

            <div class="col-md-4 mb-3">
              <label class="form-label">
                <i class="fas fa-venus-mars"></i>
                Sexo <span class="text-danger">*</span>
              </label>
              <FieldSelect 
                :options="sexoOptions" 
                v-model="dados.sexo"
              />
            </div>

            <div class="col-md-4 mb-3">
              <FieldInput 
                label="Data de Nascimento" 
                type="date" 
                :required="true" 
                v-model="dados.data_nascimento"
              />
            </div>

            <div class="col-md-4 mb-3">
              <label class="form-label">
                <i class="fas fa-ring"></i>
                Estado Civil
              </label>
              <FieldSelect 
                :options="estadoCivilOptions" 
                v-model="dados.estado_civil"
              />
            </div>

            <div class="col-md-4 mb-3">
              <FieldInput 
                label="Nacionalidade" 
                type="text" 
                :required="true" 
                v-model="dados.nacionalidade"
                placeholder="Ex: Brasileira"
              />
            </div>

            <div class="col-md-6 mb-3">
              <FieldInput 
                label="Nome da Mãe" 
                type="text" 
                :required="true" 
                v-model="dados.nome_mae"
                placeholder="Nome completo da mãe"
              />
            </div>

            <div class="col-md-6 mb-3">
              <FieldInput 
                label="Nome do Pai" 
                type="text" 
                v-model="dados.nome_pai"
                placeholder="Nome completo do pai (opcional)"
              />
            </div>
          </div>
        </div>

        <!-- Naturalidade -->
        <div class="section-container mb-4">
          <div class="section-header">
            <i class="fas fa-map-marked-alt"></i>
            <span>Naturalidade</span>
          </div>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label">
                <i class="fas fa-flag"></i>
                Estado (UF)
              </label>
              <FieldSelect 
                :options="ufs" 
                v-model="dados.natural_uf"
                placeholder="Selecione o estado"
              />
            </div>

            <div class="col-md-6 mb-3">
              <label class="form-label">
                <i class="fas fa-city"></i>
                Cidade
              </label>
              <FieldSelect 
                :options="findCities(dados.natural_uf)" 
                v-model="dados.natural_cidade"
                placeholder="Selecione a cidade"
                :disabled="!dados.natural_uf"
              />
            </div>
          </div>
        </div>

        <!-- Endereço -->
        <div class="section-container mb-4">
          <div class="section-header">
            <i class="fas fa-home"></i>
            <span>Endereço Residencial</span>
          </div>

          <div class="row">
            <div class="col-md-4 mb-3">
              <FieldInput 
                label="CEP" 
                type="text" 
                :required="true" 
                v-model="dados.cep"
                mask="#####-###"
                placeholder="00000-000"
              />
            </div>

            <div class="col-md-8 mb-3">
              <FieldInput 
                label="Logradouro" 
                type="text" 
                :required="true" 
                v-model="dados.endereco"
                placeholder="Rua, Avenida, Travessa..."
              />
            </div>

            <div class="col-md-3 mb-3">
              <FieldInput 
                label="Número" 
                type="text" 
                v-model="dados.numero"
                placeholder="Ex: 123"
              />
            </div>

            <div class="col-md-3 mb-3">
              <FieldInput 
                label="Complemento" 
                type="text" 
                v-model="dados.complemento"
                placeholder="Apto, Bloco..."
              />
            </div>

            <div class="col-md-6 mb-3">
              <FieldInput 
                label="Bairro" 
                type="text" 
                :required="true" 
                v-model="dados.bairro"
                placeholder="Nome do bairro"
              />
            </div>

            <div class="col-md-6 mb-3">
              <label class="form-label">
                <i class="fas fa-flag"></i>
                Estado (UF) <span class="text-danger">*</span>
              </label>
              <FieldSelect 
                :options="ufs" 
                v-model="dados.uf"
                placeholder="Selecione o estado"
              />
            </div>

            <div class="col-md-6 mb-3">
              <label class="form-label">
                <i class="fas fa-city"></i>
                Cidade <span class="text-danger">*</span>
              </label>
              <FieldSelect 
                :options="findCities(dados.uf)" 
                v-model="dados.cidade"
                placeholder="Selecione a cidade"
                :disabled="!dados.uf"
              />
            </div>
          </div>
        </div>

        <!-- Contatos -->
        <div class="section-container mb-4">
          <div class="section-header">
            <i class="fas fa-phone"></i>
            <span>Telefones para Contato</span>
          </div>

          <div class="row">
            <div class="col-md-4 mb-3">
              <FieldInput 
                label="Telefone Celular" 
                type="text" 
                v-model="dados.telefone_celular"
                mask="(##) #####-####"
                placeholder="(00) 00000-0000"
              />
            </div>

            <div class="col-md-4 mb-3">
              <FieldInput 
                label="Telefone Residencial" 
                type="text" 
                v-model="dados.telefone_residencial"
                mask="(##) ####-####"
                placeholder="(00) 0000-0000"
              />
            </div>

            <div class="col-md-4 mb-3">
              <FieldInput 
                label="Telefone Comercial" 
                type="text" 
                v-model="dados.telefone_comercial"
                mask="(##) ####-####"
                placeholder="(00) 0000-0000"
              />
            </div>
          </div>
        </div>

        <!-- Necessidades Especiais -->
        <!-- <div class="section-container mb-4">
          <div class="section-header">
            <i class="fas fa-universal-access"></i>
            <span>Necessidades Especiais</span>
          </div>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label">
                <i class="fas fa-wheelchair"></i>
                Possui Deficiência?
              </label>
              <FieldSelect 
                :options="booleanOptions" 
                v-model="dados.possui_deficiencia"
              />
            </div>

            <div v-if="dados.possui_deficiencia === 'S'" class="col-md-12 mb-3">
              <FieldInput 
                label="Detalhamento da Deficiência" 
                type="textarea" 
                v-model="dados.detalhe_deficiencia"
                placeholder="Descreva sua deficiência..."
              />
            </div>

            <div class="col-md-6 mb-3">
              <label class="form-label">
                <i class="fas fa-hospital"></i>
                Possui Condição Especial?
              </label>
              <FieldSelect 
                :options="booleanOptions" 
                v-model="dados.possui_condicao_especial"
              />
            </div>

            <div v-if="dados.possui_condicao_especial === 'S'" class="col-md-12 mb-3">
              <FieldInput 
                label="Detalhamento da Condição Especial" 
                type="textarea" 
                v-model="dados.detalhe_condicao_especial"
                placeholder="Descreva sua condição especial..."
              />
            </div>
          </div>
        </div> -->

        <!-- Botões -->
        <div class="text-center mt-4">
          <div class="alert alert-info mb-3">
            <i class="fas fa-info-circle"></i>
            <div class="alert-content">
              <strong>Alteração de Dados Cadastrais</strong>
              <p class="mb-0 mt-2">
                Para alterar seus dados cadastrais, entre em contato com o suporte através do e-mail:
                <a href="mailto:suporte@ibcgp.org.br" class="alert-link">suporte@ibcgp.org.br</a>
              </p>
            </div>
          </div>
          
          <button type="submit" class="btn btn-primary btn-lg" disabled>
            <i class="fas fa-lock"></i>
            Alterações Desabilitadas
          </button>
          <p class="text-muted mt-3 small">
            <i class="fas fa-envelope"></i>
            Solicite alterações pelo e-mail de suporte
          </p>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from "axios"
import estados from "@/assets/jsons/estados.json"
import FieldInput from "@/components/fields/FieldInput"
import FieldSelect from "@/components/fields/FieldSelect"

export default {
  name: 'DadosCadastrais',
  components: { FieldInput, FieldSelect },
  
  data() {
    return {
      isLoading: false,
      salvando: false,
      dados: {},
      sexoOptions: [
        { label: "Feminino", value: "F" },
        { label: "Masculino", value: "M" }
      ],
      estadoCivilOptions: [
        { label: "Solteiro(a)", value: "solteiro" },
        { label: "Casado(a)", value: "casado" },
        { label: "Divorciado(a)", value: "divorciado" },
        { label: "Viúvo(a)", value: "viúvo" }
      ],
      booleanOptions: [
        { label: "Sim", value: "S" },
        { label: "Não", value: "N" }
      ],
      ufs: estados.map(({ sigla }) => ({ label: sigla, value: sigla })),
    }
  },
  
  mounted() {
    console.log('DadosCadastrais: Componente montado')
    this.carregarDados()
  },
  
  methods: {
    carregarDados() {
      try {
        const profileData = { ...this.$store.getters["profile/getProfile"] }
        
        // Formatar data de nascimento para o formato do input type="date" (YYYY-MM-DD)
        if (profileData.data_nascimento) {
          profileData.data_nascimento = this.formatDateForInput(profileData.data_nascimento)
        }
        
        this.dados = profileData
        console.log('Dados carregados do perfil')
      } catch (error) {
        console.error('Erro ao carregar dados:', error)
      }
    },

    formatDateForInput(date) {
      if (!date) return ''
      
      try {
        // Se já estiver no formato YYYY-MM-DD, retornar como está
        if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return date
        }
        
        // Converter de formato ISO para YYYY-MM-DD
        let dateString = date
        if (typeof date === 'string' && date.includes('T')) {
          // Remover o 'Z' do final se existir e tratar como hora local
          dateString = dateString.replace('Z', '')
        }
        
        const dateObj = new Date(dateString)
        if (isNaN(dateObj.getTime())) return ''
        
        // Usar métodos locais para evitar conversão UTC
        const year = dateObj.getFullYear()
        const month = String(dateObj.getMonth() + 1).padStart(2, '0')
        const day = String(dateObj.getDate()).padStart(2, '0')
        
        return `${year}-${month}-${day}`
      } catch (error) {
        console.error('Erro ao formatar data:', error)
        return ''
      }
    },

    findCities(uf) {
      if (!uf) return []
      const estado = estados.find(e => e.sigla === uf)
      return estado ? estado.cidades.map(cidade => ({ label: cidade, value: cidade })) : []
    },
    
    async alterarDados() {
      try {
        this.salvando = true
        this.$sweet.sweetWait("Salvando suas alterações...")

        console.log('Salvando dados cadastrais...')

        await axios.put(`/ps/candidato/alterar-dados`, this.dados)
        
        // Atualizar o store com os novos dados
        this.$store.dispatch("profile/setProfile", this.dados)
        
        this.$sweet.sweetSuccess("Dados atualizados com sucesso!")
      } catch (error) {
        console.error('Erro ao atualizar dados:', error)
        
        let errorMessage = "Erro ao atualizar os dados. Tente novamente."
        
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message
        } else if (error.response?.data?.error) {
          errorMessage = error.response.data.error
        }
        
        this.$sweet.sweetError(errorMessage)
      } finally {
        this.salvando = false
      }
    }
  },
}
</script>

<style lang="sass" scoped>
.area-cadastro
  background-color: #fff
  padding: 25px 15px
  border-radius: 8px
  box-shadow: 0 2px 4px rgba(0,0,0,0.1)
  
  h5
    font-size: 20px
    font-weight: 600
    text-align: center
    color: #2c3e50
    margin-bottom: 25px
    
    i
      margin-right: 10px
      color: #007bff

// Section Container
.section-container
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)
  padding: 25px
  border-radius: 8px
  box-shadow: 0 2px 4px rgba(0,0,0,0.05)

// Section headers
.section-header
  font-size: 16px
  font-weight: 600
  color: #2c3e50
  padding: 10px 15px
  background: #fff
  border-left: 4px solid #007bff
  border-radius: 4px
  margin-bottom: 20px
  box-shadow: 0 1px 3px rgba(0,0,0,0.1)
  
  i
    margin-right: 8px
    color: #007bff
  
  span
    vertical-align: middle

// Form elements
.form-label
  font-weight: 600
  color: #2c3e50
  margin-bottom: 8px
  display: block
  font-size: 14px
  
  i
    margin-right: 6px
    color: #007bff
  
  .text-danger
    margin-left: 2px

// Loading
.is-loading
  width: 40px
  margin-top: 20px

// Buttons
.btn
  border-radius: 6px
  padding: 10px 20px
  font-weight: 500
  transition: all 0.3s ease
  border: none
  
  &:hover
    transform: translateY(-2px)
    box-shadow: 0 4px 8px rgba(0,0,0,0.2)
  
  &:disabled
    opacity: 0.6
    cursor: not-allowed
    transform: none
  
  i
    margin-right: 8px
  
  &.btn-lg
    padding: 12px 24px
    font-size: 16px

// Responsividade
@media (max-width: 768px)
  .area-cadastro
    padding: 15px 10px
    
    h5
      font-size: 18px
  
  .section-container
    padding: 15px

@media (max-width: 576px)
  .section-header
    font-size: 14px
    padding: 8px 12px
  
  .btn.btn-lg
    width: 100%

// Alert
.alert
  border-radius: 8px
  padding: 20px
  display: flex
  align-items: flex-start
  gap: 15px
  border-left: 4px solid
  
  i
    font-size: 20px
    margin-top: 2px
    flex-shrink: 0
  
  .alert-content
    flex: 1
    
    strong
      display: block
      font-size: 16px
      margin-bottom: 8px
      color: #0c5460
    
    p
      font-size: 14px
      line-height: 1.6
      color: #0c5460
      margin: 0
      
      .alert-link
        color: #0056b3
        font-weight: 600
        text-decoration: none
        
        &:hover
          text-decoration: underline
  
  &.alert-info
    background-color: #d1ecf1
    border-color: #bee5eb
    color: #0c5460
    
    i
      color: #0c5460
</style>
